import {TranslationAttributes} from 'biojs/Translation';
import {Chromosome, Genome} from 'constants/GenomeConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceServerResponse} from 'constants/SequenceConstants';
import {Optional} from 'util/Types';

enum DESIGN_TYPE {
  SINGLE = 'single',
  PAIRED = 'paired',
  BASE_EDIT = 'base_edit',
}

enum OFF_TARGET_SCORE_TYPE {
  MIT = 'MIT',
  CFD = 'CFD',
}

export enum NUCLEASE_TYPE {
  CAS9 = 'Cas9',
  CPF1 = 'Cpf1',
}

// This constant is shared with the CRISPR lambda, so make sure any updates here are also done in the lambda
// code as well at services/crispr-off-target/CrisprOffTargetLambda/constants.js.
export enum MIT_OFF_TARGET_WEIGHTS {
  UNWEIGHTED = 'UNWEIGHTED',
  SPCAS9 = 'SPCAS9',
}

export enum NUCLEASE_NAME {
  SPCAS9 = 'SpCas9',
  NMECAS9 = 'NmeCas9',
  STCAS9 = 'StCas9',
  TDCAS9 = 'TdCas9',
  SACAS9 = 'SaCas9',
  ASCPF1 = 'AsCpf1',
  LBCPF1 = 'LbCpf1',
}

export enum NUCLEASE_VARIANT {
  NG = 'NG',
  RVR = 'RVR',
  RR = 'RR',
}

export enum PAM {
  NGG = 'NGG',
  NAG = 'NAG',
  NG = 'NG',
  NNNNGATT = 'NNNNGATT',
  NNAGAAW = 'NNAGAAW',
  NAAAAC = 'NAAAAC',
  NNGRR = 'NNGRR',
  NNGRRT = 'NNGRRT',
  TTTN = 'TTTN',
  TTTV = 'TTTV',
  TYCV = 'TYCV',
  TATV = 'TATV',
}

const URLS = {
  CFD_SCORE_PAPER: 'http://www.nature.com/nbt/journal/v34/n2/full/nbt.3437.html',
  NGG_PAM: 'http://www.ncbi.nlm.nih.gov/pubmed/23873081',
  NNGRRT_PAM: 'http://www.ncbi.nlm.nih.gov/pubmed/25830891',
  HSU_OFF_TARGET_PAPER: 'https://www.nature.com/articles/nbt.2647',
  DOENCH_ACTIVITY: 'http://www.nature.com/nbt/journal/v32/n12/abs/nbt.3026.html',
};

// This is the order in which we should present PAMs in the UI.
// The most important thing is that NGG is first, since it is by far the most common.
const ORDERED_PAMS = [
  PAM.NGG,
  PAM.NAG,
  PAM.NG,
  PAM.NNNNGATT,
  PAM.NNAGAAW,
  PAM.NAAAAC,
  PAM.NNGRR,
  PAM.NNGRRT,
  PAM.TTTN,
  PAM.TTTV,
  PAM.TYCV,
  PAM.TATV,
];

const PAM_INFO_BY_PAM: {[pam in PAM]: CRISPRPam} = {
  [PAM.NGG]: {
    bases: PAM.NGG,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.SPCAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.NAG]: {
    bases: PAM.NAG,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.SPCAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.NG]: {
    bases: PAM.NG,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.SPCAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
    nucleaseVariant: NUCLEASE_VARIANT.NG,
  },
  [PAM.NNNNGATT]: {
    bases: PAM.NNNNGATT,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.NMECAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.NNAGAAW]: {
    bases: PAM.NNAGAAW,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.STCAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.NAAAAC]: {
    bases: PAM.NAAAAC,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.TDCAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.NNGRR]: {
    bases: PAM.NNGRR,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.SACAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.NNGRRT]: {
    bases: PAM.NNGRRT,
    isLeftPam: false,
    nucleaseNames: [NUCLEASE_NAME.SACAS9],
    nucleaseType: NUCLEASE_TYPE.CAS9,
  },
  [PAM.TTTN]: {
    bases: PAM.TTTN,
    isLeftPam: true,
    nucleaseNames: [NUCLEASE_NAME.ASCPF1, NUCLEASE_NAME.LBCPF1],
    nucleaseType: NUCLEASE_TYPE.CPF1,
  },
  [PAM.TTTV]: {
    bases: PAM.TTTV,
    isLeftPam: true,
    nucleaseNames: [NUCLEASE_NAME.ASCPF1, NUCLEASE_NAME.LBCPF1],
    nucleaseType: NUCLEASE_TYPE.CPF1,
  },
  [PAM.TYCV]: {
    bases: PAM.TYCV,
    isLeftPam: true,
    nucleaseNames: [NUCLEASE_NAME.ASCPF1, NUCLEASE_NAME.LBCPF1],
    nucleaseType: NUCLEASE_TYPE.CPF1,
    nucleaseVariant: NUCLEASE_VARIANT.RR,
  },
  [PAM.TATV]: {
    bases: PAM.TATV,
    isLeftPam: true,
    nucleaseNames: [NUCLEASE_NAME.ASCPF1, NUCLEASE_NAME.LBCPF1],
    nucleaseType: NUCLEASE_TYPE.CPF1,
    nucleaseVariant: NUCLEASE_VARIANT.RVR,
  },
};

export interface CRISPRPam {
  bases: string;
  isLeftPam: boolean;
  nucleaseNames: Array<string>;
  nucleaseType: NUCLEASE_TYPE;
  nucleaseVariant?: NUCLEASE_VARIANT;
}

export interface OffTargetExon {
  exonEnsemblId: string;
  geneEnsemblId: string;
  geneName: string;
}
export interface OffTargetSite {
  bases: string;
  pam: string;
  score: number;
  chromosomeName: string;
  position: number;
  strand: StrandOfDoubleStranded;
  exon: OffTargetExon;
}
export interface CRISPRGuide {
  strand: StrandOfDoubleStranded | null;
  position: number;
  bases: string;
  pam: string;
  offTargetScore: number | null;
  onTargetScore?: number;
  sites: null | Array<OffTargetSite>;
  regionId?: string;
  usedFullModel?: boolean;
}

export interface OnTargetSite {
  analysisId: string;
  start: number;
  end: number;
  id: string;
  guides: Array<unknown>;
}

export interface CRISPRRegion {
  id: string;
  start: number;
  end: number;
  crisprIaTier: number | null;
  guides: Array<CRISPRGuide>;
  genomeRegionStart: number | null;
  genomeRegionEnd: number | null;
  genomeRegionChromosome: Chromosome | null;
}

// Keep in sync with crispr_analysis.to_summary_dict()
export interface CRISPRAnalysisSummary {
  id: string;
  modifiedAt: string;
  name: string;
  sequenceId: string;
}

// Keep in sync with crispr_analysis.to_dict()
export interface CRISPRAnalysis extends CRISPRAnalysisSummary {
  originalBases: string;
  genome: Genome;
  pam: string;
  isLeftPam: boolean;
  designType: DESIGN_TYPE;
  guideLength: number;
  pairOffsetMin: number;
  pairOffsetMax: number;
  aMin: number;
  tMin: number;
  cMin: number;
  gMin: number;
  aMax: number;
  tMax: number;
  cMax: number;
  gMax: number;
  offTargetScoreType: OFF_TARGET_SCORE_TYPE;
  pamSiteOnTargetMin: number;
  pamSiteOffTargetMin: number;
  regions: Array<CRISPRRegion>;
  searchSoftMaskedBases: boolean;
  useAzimuthOnTargetScore: boolean;
}

export const ASSEMBLY_STEP = {
  SELECT_VECTOR: 'SELECT_VECTOR',
  FINALIZE_ASSEMBLIES: 'FINALIZE_ASSEMBLIES',
  COMPLETED_ASSEMBLY: 'COMPLETED_ASSEMBLY',
};

// Many of these constants are treated as enums but were introduced a long time ago.
export type HRTemplateStep = 'INTRODUCE_EDITS' | 'REMOVE_TARGET_SITE' | 'SELECT_ARMS' | 'SUMMARY';
export const HR_TEMPLATE_STEP: Record<string, HRTemplateStep> = {
  INTRODUCE_EDITS: 'INTRODUCE_EDITS',
  SELECT_ARMS: 'SELECT_ARMS',
  REMOVE_TARGET_SITE: 'REMOVE_TARGET_SITE',
  SUMMARY: 'SUMMARY',
};

export type HRTemplateCreateType = 'MODIFY' | 'CLONE';
export const HR_TEMPLATE_CREATE_TYPE: Record<string, HRTemplateCreateType> = {
  MODIFY: 'MODIFY',
  CLONE: 'CLONE',
};

export const HR_TEMPLATE_TAB_ID = 'HR_TEMPLATE_TAB_ID';
export const MINIMUM_HR_TEMPLATE_LENGTH = 50;
export const DESIRED_HR_TEMPLATE_LENGTH = 200;

// Number of mismatches recommended by Patrick Hsu between guide and HR template to prevent re-cutting
export const DESIRED_HR_MISMATCHES = 2;

export interface CRISPRConstantDefaults {
  GENOME_ENSEMBL_NAME: string;
  GENOME_PREFERENCES: Array<string>;
  PAM: PAM;
  IS_LEFT_PAM: boolean;
  DESIGN_TYPE: DESIGN_TYPE;
  GUIDE_LENGTH: number;
  PAIR_OFFSET_MIN: number;
  PAIR_OFFSET_MAX: number;
  A_MIN: number;
  A_MAX: number;
  T_MIN: number;
  T_MAX: number;
  C_MIN: number;
  C_MAX: number;
  G_MIN: number;
  G_MAX: number;
  PAM_SITE_ON_TARGET_MIN: number;
  PAM_SITE_OFF_TARGET_MIN: number;
  SHOW_CUTS_OUTSIDE_REGIONS: boolean;
  SEARCH_SOFT_MASKED_BASES: boolean;
  OFF_TARGET_SCORE_TYPE: OFF_TARGET_SCORE_TYPE;
  USE_AZIMUTH_ON_TARGET_SCORE: boolean;
}

export const DEFAULTS: CRISPRConstantDefaults = {
  GENOME_ENSEMBL_NAME: 'GRCm38',
  GENOME_PREFERENCES: ['GRCm38', 'GRCh38'],
  PAM: PAM.NGG,
  IS_LEFT_PAM: false,
  DESIGN_TYPE: DESIGN_TYPE.SINGLE,
  GUIDE_LENGTH: 20,
  // Re Patrick Hsu's paper on paired nickases
  PAIR_OFFSET_MIN: -10,
  PAIR_OFFSET_MAX: 30,
  A_MIN: 0,
  A_MAX: 80,
  T_MIN: 0,
  T_MAX: 80,
  C_MIN: 0,
  C_MAX: 80,
  G_MIN: 0,
  G_MAX: 80,
  PAM_SITE_ON_TARGET_MIN: 10,
  PAM_SITE_OFF_TARGET_MIN: 25,
  SHOW_CUTS_OUTSIDE_REGIONS: false,
  SEARCH_SOFT_MASKED_BASES: false,
  OFF_TARGET_SCORE_TYPE: OFF_TARGET_SCORE_TYPE.MIT,
  USE_AZIMUTH_ON_TARGET_SCORE: true,
};

export const PAM_TO_CUT_DISTANCE = 3;

export const IA_TYPE = {
  CRISPR_I: 'CRISPR_I',
  CRISPR_A: 'CRISPR_A',
};

export const LEFT_PAM_TO_CUT_DISTANCE = 19;

// C++ code for off-target scoring only supports PAMs between 2 and 8 bases
export const MINIMUM_PAM_LENGTH = 2;

export const MAXIMUM_PAM_LENGTH = 8;
export const MIN_GUIDE_LENGTH = 16;
export const MAX_GUIDE_LENGTH = 24;
export const MAX_OFF_TARGET_GUIDE_LENGTH = 20;
export const RECOMMENDED_AZIMUTH_ON_TARGET_GUIDE_LENGTH = 20;
export const RECOMMENDED_AZIMUTH_ON_TARGET_PAM = PAM.NGG;

export const SORT_KEY = {
  POSITION: 'POSITION',
  SPECIFICITY_PAIR: 'SPECIFICITY_PAIR',
  SPECIFICITY_SINGLE: 'SPECIFICITY_SINGLE',
  EFFICIENCY: 'EFFICIENCY',
  NUM_BASE_EDITS: 'NUM_BASE_EDITS',
  BASE_EDIT_SCORES: 'BASE_EDIT_SCORES',
  CRISPR_IA_TIER: 'CRISPR_IA_TIER',
};

export type VectorSourceType =
  | 'CREATE_FROM_FILE'
  | 'CREATE_FROM_FILE'
  | 'SELECT_EXISTING_SEQUENCE'
  | 'SELECT_VECTOR';

export const VECTOR_SOURCE_TYPE: Record<string, VectorSourceType> = {
  CREATE_FROM_FILE: 'CREATE_FROM_FILE',
  SELECT_EXISTING_SEQUENCE: 'SELECT_EXISTING_SEQUENCE',
  SELECT_VECTOR: 'SELECT_VECTOR',
};

export const BASE_EDIT_SCORING_PAM = PAM.NGG;
export const FORWARD_STRAND_EDITABLE_BASE = 'C';
export const CFD_SCORE_PAM = PAM.NGG;
export const CFD_SCORE_GUIDE_LENGTH = 20;
export const AZIMUTH_SCORE_PAM = PAM.NGG;
export {DESIGN_TYPE, OFF_TARGET_SCORE_TYPE, ORDERED_PAMS, PAM_INFO_BY_PAM, URLS};

export interface CRISPRSequence {
  id: string;
  name: string;
  length: number;
  circular: boolean;
}

/**
 * A subset of Sequence attributes picked out of a Backbone ComponentSequence for use in CRISPRAnalysisTabView
 */
export interface CRISPRAnalysisSequenceAttributes {
  id: string;
  bases: string;
  circular: boolean;
  length: number;
  startIndex: number;
  translations: Array<TranslationAttributes>;
}

export interface FileInputStateShape {
  sequence?: SequenceServerResponse | null;
  sequenceName: string;
  folderId: Optional<string>;
  importError: string | null;
}

export interface CRISPRAssemblyPrimer {
  name: string;
  bases: string;
}

export interface CRISPRVector {
  id: string;
  sequence: CRISPRSequence;
  insertionRegionStart: number;
  insertionRegionEnd: number;
  addgeneId?: number;
  fullName?: string;
}

export interface GuideGroupSort {
  key: string; // TODO: refine to SORT_KEY values
  reverse: boolean;
  offset: number;
}

export interface ValueLink<T> {
  value: T;
  requestChange: (newValue: T) => void;
}
